import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useClientSpecificContentMutation,
  useDeleteClientSpecificContentMutation,
  useGetResourceByGroupId,
} from "../../../../api/services/client/clientApis";

import { isString } from "lodash";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  WarningConfirmationModal,
} from "../../../../components";
import { MODULE_PERMISSIONS, PAGE_IDS } from "../../../../helpers/constants";
import {
  getParsedErrorMessage,
  isEmpty,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  setIsDataModifiedCurrentPage,
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";

import { ButtonWithIcon } from "../../../../components/buttonWithIcon/ButtonWithIcon";
import { ClientSpecificContent } from "../../modelsAndSchemas/Models";
import { createClientSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { AddClientSpecificContent } from "../addClientSpecificContent/AddClientSpecificContent";
import { ClientSpecificContentList } from "../clientSpecificContentList/ClientSpecificContentList";
import { ViewClientSpecificContent } from "../viewClientSpecificContent/ViewClientSpecificContent";
import { ClientContactMainWrapper } from "./ClientSpecificContentMain.style";
import { usePermissions } from "../../../../hooks/usePermissions";

export const pageId = PAGE_IDS.CREATE_CLIENT_CONTACT_RESULT;
export interface ClientContactMainProps {
  parentAccordionStatus: boolean;
  onAccordionChange: (expanded: boolean) => void;
  isViewClientSpecificContentRefProps?: boolean;
  isAddButtonVisible?: boolean;
}

export const ClientSpecificContentMain = forwardRef(
  (props: ClientContactMainProps, ref) => {
    const {
      parentAccordionStatus,
      onAccordionChange,
      isViewClientSpecificContentRefProps,
      isAddButtonVisible = true,
    } = props;
    const { fetchLabel, commonLabels, fetchComponentTitle } = useSiteCore();
    const { hasModulePermission } = usePermissions();

    const [
      clientSpecificContentAccordionStatus,
      setClientSpecificContentAccordionStatus,
    ] = useState(parentAccordionStatus);
    const [
      showAddClientSpecificContentModal,
      setShowAddClientSpecificContentModal,
    ] = useState(false);
    const [viewClientSpecificContentModal, setViewClientSpecificContentModal] =
      useState(false);
    const isEditClientSpecificContentRef = useRef(false);
    const isViewClientSpecificContentRef = useRef(
      isViewClientSpecificContentRefProps,
    );
    const [
      showClientSpecificContentDeleteModal,
      setShowClientSpecificContentDeleteModal,
    ] = useState(false);

    const indexRef = useRef<string>();
    const [clientSpecificContentData, setClientSpecificContentData] = useState<
      ClientSpecificContent[]
    >([]);

    const dispatch = useDispatch();
    const { id: clientId } = useSelector(createClientSelector);

    const {
      mutateAsync: getClientSpecificContentById,
      data: clientSpecificContentDataById,
      isPending: isGetClientSpecificContentPending,
      isSuccess: isGetClientSpecificContentSuccess,
    } = useGetResourceByGroupId();

    const {
      mutateAsync: getClientSpecificContent,
      isPending: isClientSpecificContentPending,
      isSuccess: isClientSpecificContentSuccess,
      data: clientSpecificContent,
    } = useClientSpecificContentMutation();

    const {
      mutateAsync: deleteClientSpecificContent,
      isPending: isDeleteClientSpecificContentPending,
      isSuccess: isDeleteClientSpecificContentSuccess,
      isError: isDeleteClientSpecificContentError,
      error: deleteClientSpecificContentError,
    } = useDeleteClientSpecificContentMutation();

    useEffect(() => {
      setClientSpecificContentAccordionStatus(parentAccordionStatus);
    }, [parentAccordionStatus]);

    const getLabelFromClientSpecificContent = (key: string) => {
      return fetchLabel(
        createClientSiteCoreKeys.clientSpecificContentTitle,
        key,
      );
    };

    useEffect(() => {
      if (isDeleteClientSpecificContentSuccess) {
        dispatch(
          toggleToast({
            open: true,
            message: getLabelFromClientSpecificContent(
              createClientSiteCoreKeys.groupDeletedMessage,
            ),
          }),
        );
        dispatch(setIsDataModifiedCurrentPage(true));
        getClientSpecificContentApi();
      }
    }, [isDeleteClientSpecificContentSuccess]);
    useEffect(() => {
      dispatch(
        toggleLoader({
          isVisible: isDeleteClientSpecificContentPending,
        }),
      );
    }, [isDeleteClientSpecificContentPending]);

    useEffect(() => {
      if (
        isDeleteClientSpecificContentError &&
        deleteClientSpecificContentError
      ) {
        dispatch(
          toggleToast({
            open: true,
            message: getParsedErrorMessage(deleteClientSpecificContentError),
          }),
        );
      }
    }, [isDeleteClientSpecificContentError, deleteClientSpecificContentError]);

    useEffect(() => {
      if (isEmpty(clientId)) return;
      getClientSpecificContentApi();
    }, [clientId]);

    useImperativeHandle(ref, () => ({
      getClientSpecificContentApi,
    }));

    useEffect(() => {
      if (isGetClientSpecificContentSuccess) {
        if (isViewClientSpecificContentRef.current) {
          setViewClientSpecificContentModal(true);
        } else {
          isEditClientSpecificContentRef.current = true;
          setShowAddClientSpecificContentModal(true);
        }
      }
    }, [isGetClientSpecificContentSuccess]);

    useEffect(() => {
      dispatch(
        toggleLoader({
          isGetClientSpecificContentPending: {
            isVisible: isGetClientSpecificContentPending,
            message: commonLabels.loadingPleaseWait,
          },
        }),
      );
      return () => {
        dispatch(
          toggleLoader({
            isGetClientSpecificContentPending: {
              isVisible: false,
            },
          }),
        );
      };
    }, [isGetClientSpecificContentPending]);

    useEffect(() => {
      dispatch(
        toggleLoader({
          isVisible: isClientSpecificContentPending,
          message: commonLabels.loadingPleaseWait,
        }),
      );
    }, [isClientSpecificContentPending]);

    useEffect(() => {
      if (isClientSpecificContentSuccess) {
        setClientSpecificContentData(clientSpecificContent.data);
        if (
          !clientSpecificContentAccordionStatus &&
          isNotEmpty(clientSpecificContent.data) &&
          isAddButtonVisible
        ) {
          onAccordionChange(true);
        }
      }
    }, [isClientSpecificContentSuccess]);

    const getClientSpecificContentApi = () => {
      getClientSpecificContent(clientId);
    };

    const handleClientSpecificContentAdded = (groupId?: string) => {
      getClientSpecificContent(clientId);
      if (groupId && isNotEmpty(groupId)) {
        handleViewClientSpecificContent(groupId);
      }
      dispatch(setIsDataModifiedCurrentPage(true));
    };

    const getClientSpecificContentByGroupId = (groupId: string) => {
      getClientSpecificContentById(groupId);
    };

    const handleEditClientSpecificContentFromView = (groupId: string) => {
      isViewClientSpecificContentRef.current = false;
      isEditClientSpecificContentRef.current = true;
      setViewClientSpecificContentModal(false);
      getClientSpecificContentByGroupId(groupId);
    };

    const handleEditClientSpecificContent = (
      clientSpecificContent: ClientSpecificContent,
    ) => {
      isViewClientSpecificContentRef.current = false;
      isEditClientSpecificContentRef.current = true;
      getClientSpecificContentByGroupId(
        clientSpecificContent.clientResourceGroupId,
      );
    };

    const handleViewClientSpecificContent = (
      clientSpecificContent: ClientSpecificContent | string,
    ) => {
      isViewClientSpecificContentRef.current = true;
      getClientSpecificContentByGroupId(
        isString(clientSpecificContent)
          ? clientSpecificContent
          : clientSpecificContent.clientResourceGroupId,
      );
    };

    const handleAddClientSpecificContentClick = () => {
      if (!showAddClientSpecificContentModal) {
        isEditClientSpecificContentRef.current = false;
        setShowAddClientSpecificContentModal(true);
      }
    };

    const handleDeleteClientSpecificContentClick = () => {
      const clientResourceGroupId = indexRef.current ?? "";
      if (isNotEmpty(clientResourceGroupId)) {
        setShowClientSpecificContentDeleteModal(false);
        deleteClientSpecificContent({ clientResourceGroupId });
      }
    };

    const deleteClientSpecificContentAction = (row: ClientSpecificContent) => {
      if (!showClientSpecificContentDeleteModal) {
        indexRef.current = row.clientResourceGroupId;
        setShowClientSpecificContentDeleteModal(true);
      }
    };

    const handleModalClose = () => {
      if (showClientSpecificContentDeleteModal) {
        setShowClientSpecificContentDeleteModal(false);
      }
    };

    const handleClientSpecificContentListChange = (
      _event: React.SyntheticEvent,
      expanded: boolean,
    ) => {
      setClientSpecificContentAccordionStatus(expanded);
      onAccordionChange(expanded);
    };

    return (
      <ClientContactMainWrapper>
        <AccordionHeader
          title={
            <AccordionHeaderText
              label={fetchComponentTitle(
                createClientSiteCoreKeys.clientSpecificContentTitle,
              )}
            />
          }
          onChange={handleClientSpecificContentListChange}
          isExpanded={clientSpecificContentAccordionStatus}
        >
          {isAddButtonVisible &&
            hasModulePermission(
              MODULE_PERMISSIONS.ADD_CLIENT_SPECIFIC_CONTENT,
            ) && (
              <MuiBox className="semi-header">
                <ButtonWithIcon
                  buttonText={fetchLabel(
                    createClientSiteCoreKeys.clientSpecificContentTitle,
                    createClientSiteCoreKeys.addClientSpecificContentButton,
                  )}
                  onActionClick={handleAddClientSpecificContentClick}
                />
              </MuiBox>
            )}
          <ClientSpecificContentList
            rows={clientSpecificContentData || []}
            onClientSpecificContentEdit={handleEditClientSpecificContent}
            onClientSpecificContentView={handleViewClientSpecificContent}
            onClientSpecificContentDelete={deleteClientSpecificContentAction}
            isEditable={isAddButtonVisible}
          />
        </AccordionHeader>
        {showAddClientSpecificContentModal && (
          <AddClientSpecificContent
            show={showAddClientSpecificContentModal}
            onClose={() => setShowAddClientSpecificContentModal(false)}
            onClientSpecificContentAdded={handleClientSpecificContentAdded}
            isEdit={isEditClientSpecificContentRef.current}
            clientSpecificContentData={clientSpecificContentDataById}
          />
        )}
        {viewClientSpecificContentModal && (
          <ViewClientSpecificContent
            show={viewClientSpecificContentModal}
            onClose={() => setViewClientSpecificContentModal(false)}
            clientSpecificContentData={clientSpecificContentDataById}
            isEditable={isAddButtonVisible}
            onClientSpecificContentEdit={
              handleEditClientSpecificContentFromView
            }
          />
        )}

        {showClientSpecificContentDeleteModal && (
          <WarningConfirmationModal
            show={showClientSpecificContentDeleteModal}
            modalTitle={commonLabels.confirmation}
            onClose={handleModalClose}
            secondaryButtonTitle={commonLabels.no}
            onPrimaryButtonClick={handleDeleteClientSpecificContentClick}
            labelContent={getLabelFromClientSpecificContent(
              createClientSiteCoreKeys.deleteGroupActionText,
            )}
          />
        )}
      </ClientContactMainWrapper>
    );
  },
);
